import React, { useEffect, useRef, useState } from 'react';
import { ScissorOutlined } from '@ant-design/icons';

import { formatDuration } from 'helpers/time';
import styles from 'pages/AIClips/AIClips.module.scss';

interface LazyThumbProps {
  thumbUrl: string | null;
  clipUrl: string | null;
  idx: number;
  duration?: number;
}

function renderThumbContent(
  visible: boolean,
  thumbUrl: string | null,
  clipUrl: string | null,
  idx: number
): React.ReactElement {
  if (!visible) {
    return (
      <div className={styles.thumbPlaceholder}>
        <ScissorOutlined />
      </div>
    );
  }
  if (thumbUrl) {
    return <img src={thumbUrl} alt={`Clip ${idx + 1}`} loading="lazy" />;
  }
  if (clipUrl) {
    return (
      <video
        src={`${clipUrl}#t=0.5`}
        preload="metadata"
        muted
        className={styles.clipThumbVideo}
      />
    );
  }
  return (
    <div className={styles.thumbPlaceholder}>
      <ScissorOutlined />
    </div>
  );
}

const LazyThumb: React.FC<LazyThumbProps> = React.memo(function LazyThumb({
  thumbUrl,
  clipUrl,
  idx,
  duration,
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin: '100px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className={styles.clipThumb}>
      {renderThumbContent(visible, thumbUrl, clipUrl, idx)}
      {duration != null && (
        <div className={styles.durationBadge}>{formatDuration(duration)}</div>
      )}
    </div>
  );
});

export default LazyThumb;
